import RenderManager from "../rendering/render-manager";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";
import Base2DElementDynamic from "./base-2d-element-dynamic";
import getTabIndex from "../utils/tab-index";

let landScapeMultiplier;
class Button extends Base2DElementDynamic {

    get type2d() { return "Button"; }

    _createContainer() {
        const container = super._createContainer();

        container.classList.add("q3-button");

        if (!Q3.editMode) {
            container.classList.add('viewer-button');
            container.tabIndex = getTabIndex();
        }

        return container;
    }

    _initDefaultVars() {
        super._initDefaultVars();

        this._wh = new THREE.Vector2(150, 60).divide(RenderManager.getSize());
        this._content = "";
        this._fontSize = 30;
        this._color = null;
        this._background = null;
        this._padding = 10;
        this._paddingX = null;
        this._paddingY = 10;
        this._borderWidth = null;
        this._borderRadius = null;
        this._borderColor = null;
        this._resizeable = false;
    }

    get supportsActions() { return true; }

    get content() { return this._content; }
    set content(val) { this._content = val; }

    get fontSize() { return this._fontSize*(Q3.isMobileLandscape ? 1.25 : 1); }
    set fontSize(val) { this._fontSize = val; }
    get color() { return this._color; }
    set color(val) { this._color = val; }
    get background() { return this._background; }
    set background(val) { this._background = val; }
    get padding() { return this._padding*(Q3.isMobileLandscape ? 0.65 : 1); }
    set padding(val) { this._padding = val; }
    get paddingX() { return this._paddingX*(Q3.isMobileLandscape ? 0.65 : 1); }
    set paddingX(val) { this._paddingX = val; }
    get paddingY() { return this._paddingY*(Q3.isMobileLandscape ? 0.65 : 1); }
    set paddingY(val) { this._paddingY = val; }
    get borderWidth() { return this._borderWidth; }
    set borderWidth(val) { this._borderWidth = val; }
    get borderRadius() { return this._borderRadius; }
    set borderRadius(val) { this._borderRadius = val; }
    get borderColor() { return this._borderColor; }
    set borderColor(val) { this._borderColor = val; }
    get resizeable() { return this._resizeable; }
    set resizeable(val) {  }

    _initValues(options) {
        this.parentLayer.domElement.style.overflow = "visible";

        this._content = "content" in options ? options.content : this._content;

        /* SPECIFIC 2D ELEMENTS PARAMETERS */
        // this._fontSize = "fontSize" in options ? options.fontSize : this._fontSize; //force 30
        this._color = "color" in options ? options.color : this._color;
        this._background = "background" in options ? options.background : this._background;
        this._padding = "padding" in options ? options.padding : this._padding;
        this._paddingX = "paddingX" in options ? options.paddingX : null;
        this._borderWidth = "borderWidth" in options ? options.borderWidth : this._borderWidth;
        this._borderRadius = "borderRadius" in options ? options.borderRadius : this._borderRadius;
        this._borderColor = "borderColor" in options ? options.borderColor : this._borderColor;

        // this._sizeSm.fontSize = options.sizeSm && "fontSize" in options.sizeSm ? options.sizeSm.fontSize : this.fontSize;
        // this._sizeLg.fontSize = options.sizeLg && "fontSize" in options.sizeLg ? options.sizeLg.fontSize : this.fontSize;

        this._sizeSm = options.sizeSm ? options.sizeSm : {
            x: 0.05,
            y: 0.05,
            fontSize: this._fontSize,
            width: 0.3,
            height: 0.13,
        };
        this.data.sizeSm = this.data.sizeSm || this._sizeSm;
        this.data.sizeSm.fontSize = this._sizeSm.fontSize = this._fontSize;
        this._sizeLg = options.sizeLg ? options.sizeLg : {
            x: 0.15,
            y: 0.15,
            fontSize: this._fontSize,
            width: 0.15,
            height: 0.065,
        };
        this.data.sizeLg = this.data.sizeLg || this._sizeLg;
        this.data.sizeLg.fontSize = this._sizeLg.fontSize = this._fontSize;
    }

    constructor(options, parentLayer, uuid) {

        super(options, parentLayer, uuid);

        this.domElement.innerHTML = `<span>${this.content}</span>`;

        if (Q3.isReady) {
            this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
            this.enterExitCtrl.init(options);
        } else {
            Q3.Events.listen("tourReady", () => {
                this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
                this.enterExitCtrl.init(options);
            });
        }

        if (Q3.editMode) {
            this.domElement.addEventListener('click', e => {
                this.selectHandler(e);
            }, false);
        }

        this.redraw = this.redraw.bind(this);
        this._setPlayPauseButtonStyleThis = this.setPlayPauseButtonStyle.bind(this);
        this.domElement.style.cursor = "pointer";
        this.handleListeners(true);

        this.redraw();

    }

    handleListeners(add) {
        Events[add ? "listen" : "unlisten"]("viewportchanged", this.redraw);
        Events[add ? "listen" : "unlisten"]("splashremoved", this.redraw);
        Q3.mixer.actionStatusChanged[add ? "listen" : "unlisten"](this._setPlayPauseButtonStyleThis);
    }


    playLeavingAnimation() {
        this.enterExitCtrl.playLeave();
    }

    cancelLeavingAnimation() {
        this.enterExitCtrl.cancelLeave();
    }

    hide() {

        this.textElement.classList.add('2d-element-hidden');

    }

    show() {
        this.textElement.classList.remove('2d-element-hidden');
    }

    hexToRgba(hex, alpha) {
        let c = new THREE.Color(hex).toArray();
        return "rgba(" + c[0] * 255 + "," + c[1] * 255 + "," + c[2] * 255 + "," + alpha + ")";
    }

    getBBox() {
        const bbox = this.domElement.getBoundingClientRect();

        let x, y;

        if (!Q3.editMode) {
            x = this.position * 100;
            y = this.position * 100;
        }

        return { x, y, w: bbox.width + x, h: bbox.height + y };
    }

    setText(text) {
        this.content = text;
        this.data.content = text;
        this.domElement.innerHTML = `<span>${text}</span>`;
        Events.fire("objchanged");
    }

    getRenderPositionAndSize(width, height) {
        const size = RenderManager.getSize();

        if (!width) { width = size.width; }
        if (!height) { height = size.height; }

        if (!Q3.editMode && this.inLetterbox !== false) {
            width = Q3.letterbox.offsetWidth / RenderManager.pixelRatio;
            height = (Q3.letterbox.offsetHeight) / RenderManager.pixelRatio;
        }

        let w = this.width * width * RenderManager.pixelRatio;
        let h = this.height * height * RenderManager.pixelRatio;

        // if (w < 80) { w = 80; }
        // if (h < 40) { h = 40; }

        const [x, y] = this._calcPosition(width, height);

        return { x, y, w, h };
    }

    redraw(width, height) {

        !width && (width = RenderManager.domElement.width / RenderManager.pixelRatio);
        !height && (height = (RenderManager.domElement.height) / RenderManager.pixelRatio);

        if (!Q3.editMode) {
            width = Q3.letterbox.offsetWidth / RenderManager.pixelRatio;
            height = (Q3.letterbox.offsetHeight) / RenderManager.pixelRatio;
        }

        let currentSize = this.getVWSize();
        if (this[currentSize]) {
            Object.assign(this, { x: this[currentSize].x, y: this[currentSize].y, fontSize: this[currentSize].fontSize });
        }

        let padding = `${(0.15 + 0.055 * this.padding) + "em"} ${(0.25 + 0.175 * (this.paddingX ? this.paddingX : this.padding)) + "em"}`
        let style = {
            fontSize: this.fontSize / 24 + "rem",
            color: this.color,
            padding: padding,
            borderRadius: this.borderRadius / 10 + "rem",
            background: this.background,
            borderWidth: this.borderWidth + "px",
            borderColor: this.hexToRgba(this.borderColor, 0.75)
        };

        if (Q3.editMode) {
            if (Q3.mobileView && this.hiddenMobile) {
                this.domElement.style.opacity = 0.4;
            } else if (!Q3.mobileView && this.hiddenDesktop) {
                this.domElement.style.opacity = 0.4;
            } else {
                this.domElement.style.opacity = 1;
            }
        }

        Object.assign(this.domElement.style, style);

        let [x, y] = this._calcPosition(width, height);
        let w,h;
        this.domElement.style.position = "absolute";
        this.domElement.style.top = Math.round(y) + "px";
        this.domElement.style.left = Math.round(x) + "px";
        this.setPlayPauseButtonStyle();
        if (Q3.editMode) {
            this.domElement.classList[(Q3.selected === this && !Q3.multiSelect[this.uuid]) ? "add" : "remove"]("is-selected")
        }
        if (this.objChanged) {
            Events.fire("objchanged");
            this.objChanged = false;
        }
    }

    setPlayPauseButtonStyle() {
        let layerData = Q3.data.layers.layers2D[this.parentLayer.uuid];
        if (!layerData || !layerData.objects[this.uuid]) return
        const action = this.data.action;
        if (action) {
            for (let i in action) {
                const currAction = action[i];
                if (currAction.systemAction === "playpause") {
                    const isPlaying = Q3.mixer.getObjectAnimationStatus(currAction.value.objectUuid) === Q3.mixer.Status.PLAYING;
                    this.domElement.classList[isPlaying ? "add" : "remove"]("is-playing");
                    this.domElement.classList[isPlaying ? "remove" : "add"]("is-paused");
                    return;
                }
                if (currAction.systemAction === "playpausevideo") {
                    if (currAction.value.objectUuid && Q3.data.layers.layers2D[currAction.value.objectUuid]) {
                        let currentVideoAutoplay = Object.values(Q3.data.layers.layers2D[currAction.value.objectUuid].objects)[0].autoplay;
                        if (currentVideoAutoplay !== false) {
                            this.setPlayStyle();
                        } else {
                            this.setStopStyle()
                        }
                    }
                    return;
                }
                else {
                    this.domElement.classList.remove("is-playing");
                    this.domElement.classList.remove("is-paused");
                }
            }
        }
    }

    setPlayStyle(){
        this.domElement.classList.add('is-playing');
        this.domElement.classList.remove('is-paused');
    }

    setStopStyle(){
        this.domElement.classList.add('is-paused');
        this.domElement.classList.remove('is-playing');
    }

    delete() {

        this.handleListeners(false);

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }

    }

    clone() {

        const button = new Button({
            content: this.content,
            x: this.x + 0.01,
            y: this.y + 0.01,
        },
            this.parentLayer,
            THREE.Math.generateUUID()
        );

        Q3.data.layers.layers2D[button.parentLayer.uuid].objects[button.uuid] = {
            type: 'text',
            content: button.content,
            x: button.x,
            y: button.y,
        };

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 0, button);

        return button;

    }
    setData(data) {
        delete data.width;
        delete data.height;
        let currentSize = this.getVWSize();

        Object.assign(this[currentSize], data);

        const object = this.data;

        if (!object[currentSize]) { object[currentSize] = {}; }

        Object.assign(object[currentSize], data);

        this.redraw();
    }

    get width() {
        return this.domElement.offsetWidth / Q3.container.offsetWidth;
    }

    set width(val) {
        this._wh.x = val;
    }

    get height() {
        return this.domElement.offsetHeight / Q3.container.offsetHeight;
    }

    set height(val) {
        this._wh.y = val;
    }
}

export default Button;
export { Button };
